import { useTranslation } from "react-i18next";

import { Modal } from "../../../../shared/ui/modal";
import { CUSTOM_FIELD_ERRORS, useCenterCourses, useCenterMembers, useCenterPatientFields, useEnrollStudents } from "../../../center";
import type { StudentInput } from "../../../center";
import { PATIENT_COLUMNS, STUDENT_COLUMNS, customFieldColumns, readPatients, readStudents } from "../../domain/csvImports";
import { STUDENT_ERRORS } from "../refusals";
import CsvImportPanel, { type CsvOutcome } from "../../../../shared/ui/csv/CsvImportPanel";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    centerId: string;
    /** A school's file names a course; a therapy centre's names couples as well as groups. */
    isTraining: boolean;
}

/**
 * Taking students on from a spreadsheet: one row each, named by course and
 * mentor as the staff know them. Rows sharing a `grupo` become a group.
 */
export default function StudentImportModal({ isOpen, onClose, centerId, isTraining }: Props) {
    const { t } = useTranslation();
    const { courses } = useCenterCourses(centerId);
    const { members } = useCenterMembers(centerId, true);
    const mentors = members.filter((member) => member.roles.includes("therapist"));
    const enroll = useEnrollStudents(centerId);
    const { fields } = useCenterPatientFields(centerId);
    const fixedColumns = isTraining ? STUDENT_COLUMNS : PATIENT_COLUMNS;
    // The centre's own fields, after the file's; each described by its label and type.
    const customColumns = customFieldColumns(fields, fixedColumns).map((column) => ({
        ...column,
        description: `${column.field.label} · ${t(`center.patientFields.types.${column.field.field_type}`)}${
            column.field.field_type === "select" ? `: ${column.field.options.join(", ")}` : ""
        }`,
    }));
    const columns = [...fixedColumns, ...customColumns];
    const read = isTraining ? readStudents : readPatients;
    const errorText = (code: string, outcome: CsvOutcome) => {
        const customField = code.startsWith("custom_field_") ? CUSTOM_FIELD_ERRORS[code] : undefined;
        if (!customField) return t(STUDENT_ERRORS[code] ?? "center.students.errors.generic");
        const label = fields.find((field) => field.id === outcome.field_id)?.label;
        return label ? t("center.csv.customFieldRefusal", { field: label, reason: t(customField) }) : t(customField);
    };

    const hint = (
        <div className="flex flex-col gap-2">
            <p>{t(isTraining ? "center.students.csvHintCourse" : "center.students.csvHint")}</p>
            {isTraining && courses.length > 0 && (
                <div>
                    <p className="font-medium text-content-primary">{t("center.students.courseNames")}</p>
                    <p className="mt-1 select-all font-mono">{courses.map((course) => course.internal_name).join(", ")}</p>
                </div>
            )}
            {customColumns.length > 0 && <p>{t("center.students.csvCustomFieldsHint")}</p>}
            {mentors.length > 0 && (
                <div>
                    <p className="font-medium text-content-primary">{t("center.students.mentorEmails")}</p>
                    <p className="mt-1 select-all font-mono">{mentors.map((m) => m.email).join(", ")}</p>
                </div>
            )}
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            ariaLabel={t("center.csv.students.title")}
            className="mx-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-surface-raised p-6"
        >
            <h3 className="text-lg font-semibold text-content-primary">{t("center.csv.students.title")}</h3>
            <p className="mb-5 mt-1 text-sm text-content-tertiary">
                {t(isTraining ? "center.csv.students.subtitle" : "center.csv.students.subtitleCouples")}
            </p>
            <CsvImportPanel<StudentInput>
                columns={columns}
                read={(table) => read(table, customColumns)}
                templateName={isTraining ? "alumnos" : "pacientes"}
                templateRows={[
                    columns.map((column) =>
                        column.key === "curso" ? (courses[0]?.internal_name ?? column.example) : column.example
                    ),
                ]}
                hint={hint}
                onImport={(students) => enroll.mutateAsync(students)}
                describe={(student) => `${student.first_name} ${student.last_name}`}
                errorMessage={errorText}
                submitLabel={(count) => t("center.students.csvSubmit", { count })}
            />
        </Modal>
    );
}
